import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash2, UserSquare2, Pencil } from "lucide-react";

const AFP = ["INTEGRA", "HABITAT", "PRIMA", "PROFUTURO", "ONP"];
const fmt = (n) => Number(n || 0).toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function Empleados() {
  const { empresa, formatApiError } = useApp();
  const [empleados, setEmpleados] = useState([]);
  const [planillas, setPlanillas] = useState([]);
  const [openEmp, setOpenEmp] = useState(false);
  const [openPla, setOpenPla] = useState(false);
  const [editing, setEditing] = useState(null);
  const blankEmp = { dni: "", nombres: "", apellidos: "", cargo: "", fecha_ingreso: new Date().toISOString().slice(0, 10), sueldo_basico: 1130, afp: "INTEGRA", activo: true };
  const [formE, setFormE] = useState(blankEmp);
  const blankPla = { periodo: new Date().toISOString().slice(0, 7), empleado_id: "", sueldo_basico: 1130, asignacion_familiar: 0, horas_extras: 0, bonificaciones: 0, descuentos_otros: 0 };
  const [formP, setFormP] = useState(blankPla);
  const [periodoFiltro, setPeriodoFiltro] = useState(new Date().toISOString().slice(0, 7));

  const load = async () => {
    const [{ data: e }, { data: p }] = await Promise.all([api.get("/empleados"), api.get(`/planillas?periodo=${periodoFiltro}`)]);
    setEmpleados(e); setPlanillas(p);
  };
  useEffect(() => { if (empresa) load(); /* eslint-disable-next-line */ }, [empresa, periodoFiltro]);

  const submitEmp = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formE, sueldo_basico: +formE.sueldo_basico };
      if (editing) await api.put(`/empleados/${editing.id}`, payload);
      else await api.post("/empleados", payload);
      toast.success("Guardado"); setOpenEmp(false); setEditing(null); setFormE(blankEmp); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submitPla = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formP, sueldo_basico: +formP.sueldo_basico, asignacion_familiar: +formP.asignacion_familiar, horas_extras: +formP.horas_extras, bonificaciones: +formP.bonificaciones, descuentos_otros: +formP.descuentos_otros };
      await api.post("/planillas", payload); toast.success("Planilla generada"); setOpenPla(false); setFormP(blankPla); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const pickEmpleado = (id) => {
    const emp = empleados.find((x) => x.id === id);
    if (emp) setFormP((p) => ({ ...p, empleado_id: id, sueldo_basico: emp.sueldo_basico }));
  };

  const editarEmp = (e) => { setEditing(e); setFormE(e); setOpenEmp(true); };
  const eliminarEmp = async (e) => { if (!confirm("¿Eliminar?")) return; await api.delete(`/empleados/${e.id}`); toast.success("Eliminado"); load(); };
  const eliminarPla = async (p) => { if (!confirm("¿Eliminar?")) return; await api.delete(`/planillas/${p.id}`); toast.success("Eliminado"); load(); };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">Empleados y Planillas</h1>
        <p className="text-[13px] text-slate-500">Gestión de personal, boletas de pago y aportes (AFP/ONP, EsSalud).</p>
      </div>

      <Tabs defaultValue="empleados">
        <TabsList>
          <TabsTrigger value="empleados" data-testid="tab-empleados">Empleados</TabsTrigger>
          <TabsTrigger value="planillas" data-testid="tab-planillas">Planillas</TabsTrigger>
        </TabsList>

        <TabsContent value="empleados" className="space-y-3">
          <div className="flex justify-end">
            <Dialog open={openEmp} onOpenChange={(v) => { setOpenEmp(v); if (!v) { setEditing(null); setFormE(blankEmp); } }}>
              <DialogTrigger asChild><Button className="h-9 bg-slate-900 text-white"><Plus className="w-4 h-4 mr-1" /> Nuevo empleado</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{editing ? "Editar" : "Nuevo"} empleado</DialogTitle></DialogHeader>
                <form onSubmit={submitEmp} className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <Label className="text-[12px]">DNI</Label>
                    <Input value={formE.dni} onChange={(e) => setFormE({ ...formE, dni: e.target.value })} required maxLength={8} className="h-9 mono" />
                  </div>
                  <div>
                    <Label className="text-[12px]">Fecha ingreso</Label>
                    <Input type="date" value={formE.fecha_ingreso} onChange={(e) => setFormE({ ...formE, fecha_ingreso: e.target.value })} className="h-9" />
                  </div>
                  <div>
                    <Label className="text-[12px]">Nombres</Label>
                    <Input value={formE.nombres} onChange={(e) => setFormE({ ...formE, nombres: e.target.value })} required className="h-9" />
                  </div>
                  <div>
                    <Label className="text-[12px]">Apellidos</Label>
                    <Input value={formE.apellidos} onChange={(e) => setFormE({ ...formE, apellidos: e.target.value })} required className="h-9" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-[12px]">Cargo</Label>
                    <Input value={formE.cargo} onChange={(e) => setFormE({ ...formE, cargo: e.target.value })} className="h-9" />
                  </div>
                  <div>
                    <Label className="text-[12px]">Sueldo básico</Label>
                    <Input type="number" step="0.01" value={formE.sueldo_basico} onChange={(e) => setFormE({ ...formE, sueldo_basico: e.target.value })} className="h-9 tabular text-right" />
                  </div>
                  <div>
                    <Label className="text-[12px]">Sistema pensiones</Label>
                    <Select value={formE.afp} onValueChange={(v) => setFormE({ ...formE, afp: v })}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>{AFP.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <DialogFooter className="col-span-2"><Button type="submit" className="bg-slate-900 text-white">Guardar</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          <div className="bg-white border border-slate-200 rounded-md">
            <table className="tbl w-full">
              <thead><tr><th>DNI</th><th>Apellidos y Nombres</th><th>Cargo</th><th>Ingreso</th><th>Pensión</th><th className="text-right">Sueldo</th><th></th></tr></thead>
              <tbody>
                {empleados.length === 0 ? <tr><td colSpan={7} className="text-center py-6 text-slate-500"><UserSquare2 className="w-8 h-8 mx-auto mb-2 text-slate-300" />Sin empleados</td></tr>
                  : empleados.map((e) => (
                    <tr key={e.id}>
                      <td className="mono">{e.dni}</td>
                      <td className="font-medium text-slate-800">{e.apellidos}, {e.nombres}</td>
                      <td className="text-slate-600">{e.cargo}</td>
                      <td className="mono">{e.fecha_ingreso?.slice(0, 10)}</td>
                      <td><span className="badge badge-info">{e.afp}</span></td>
                      <td className="tabular text-right font-medium">{fmt(e.sueldo_basico)}</td>
                      <td className="text-right">
                        <button onClick={() => editarEmp(e)} className="text-slate-600 hover:text-blue-600 mr-2"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => eliminarEmp(e)} className="text-slate-600 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="planillas" className="space-y-3">
          <div className="flex items-center justify-between">
            <Input type="month" value={periodoFiltro} onChange={(e) => setPeriodoFiltro(e.target.value)} className="h-9 w-44" />
            <Dialog open={openPla} onOpenChange={(v) => { setOpenPla(v); if (!v) setFormP(blankPla); }}>
              <DialogTrigger asChild><Button className="h-9 bg-slate-900 text-white"><Plus className="w-4 h-4 mr-1" /> Generar planilla</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Generar boleta de pago</DialogTitle></DialogHeader>
                <form onSubmit={submitPla} className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <Label className="text-[12px]">Periodo</Label>
                    <Input type="month" value={formP.periodo} onChange={(e) => setFormP({ ...formP, periodo: e.target.value })} className="h-9" />
                  </div>
                  <div>
                    <Label className="text-[12px]">Empleado</Label>
                    <Select value={formP.empleado_id} onValueChange={pickEmpleado}>
                      <SelectTrigger className="h-9"><SelectValue placeholder="Empleado" /></SelectTrigger>
                      <SelectContent>{empleados.map((e) => <SelectItem key={e.id} value={e.id}>{e.apellidos}, {e.nombres}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[12px]">Sueldo básico</Label>
                    <Input type="number" step="0.01" value={formP.sueldo_basico} onChange={(e) => setFormP({ ...formP, sueldo_basico: e.target.value })} className="h-9 tabular text-right" />
                  </div>
                  <div>
                    <Label className="text-[12px]">Asignación familiar</Label>
                    <Input type="number" step="0.01" value={formP.asignacion_familiar} onChange={(e) => setFormP({ ...formP, asignacion_familiar: e.target.value })} className="h-9 tabular text-right" />
                  </div>
                  <div>
                    <Label className="text-[12px]">Horas extras</Label>
                    <Input type="number" step="0.01" value={formP.horas_extras} onChange={(e) => setFormP({ ...formP, horas_extras: e.target.value })} className="h-9 tabular text-right" />
                  </div>
                  <div>
                    <Label className="text-[12px]">Bonificaciones</Label>
                    <Input type="number" step="0.01" value={formP.bonificaciones} onChange={(e) => setFormP({ ...formP, bonificaciones: e.target.value })} className="h-9 tabular text-right" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-[12px]">Otros descuentos</Label>
                    <Input type="number" step="0.01" value={formP.descuentos_otros} onChange={(e) => setFormP({ ...formP, descuentos_otros: e.target.value })} className="h-9 tabular text-right" />
                  </div>
                  <DialogFooter className="col-span-2"><Button type="submit" className="bg-slate-900 text-white">Generar</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          <div className="bg-white border border-slate-200 rounded-md">
            <table className="tbl w-full">
              <thead><tr><th>Periodo</th><th>Empleado</th><th>Pensión</th><th className="text-right">Ingresos</th><th className="text-right">Desc.</th><th className="text-right">EsSalud</th><th className="text-right">Neto a pagar</th><th></th></tr></thead>
              <tbody>
                {planillas.length === 0 ? <tr><td colSpan={8} className="text-center py-6 text-slate-500">Sin planillas en este periodo</td></tr>
                  : planillas.map((p) => (
                    <tr key={p.id}>
                      <td className="mono">{p.periodo}</td>
                      <td className="font-medium text-slate-800">{p.empleado?.apellidos}, {p.empleado?.nombres}<div className="text-[10px] mono text-slate-500">DNI {p.empleado?.dni}</div></td>
                      <td><span className="badge badge-info">{p.empleado?.afp}</span></td>
                      <td className="tabular text-right">{fmt(p.total_ingresos)}</td>
                      <td className="tabular text-right text-rose-600">- {fmt(p.total_descuentos)}</td>
                      <td className="tabular text-right">{fmt(p.aporte_essalud)}</td>
                      <td className="tabular text-right font-bold">{fmt(p.neto_pagar)}</td>
                      <td className="text-right"><button onClick={() => eliminarPla(p)} className="text-slate-600 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button></td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
